import React, { createContext, useState, useContext, useEffect } from "react";
import en from "../assets/flags/en.png";
import ru from "../assets/flags/ru.png";
import hy from "../assets/flags/hy.png";

import ruJson from "./ru.json";
import enJson from "./en.json";
import hyJson from "./hy.json";

const mapTranslations = (arr) => {
	if (!Array.isArray(arr)) return arr || {};
	return arr.reduce((acc, item) => {
		acc[item.code] = item.val;
		return acc;
	}, {});
};

const translations = {
	ru: mapTranslations(ruJson),
	en: mapTranslations(enJson),
	hy: mapTranslations(hyJson),
};

const DEFAULT_LANGUAGE = "ru";

// The picker offers three languages, so an unknown or absent stored value has to
// resolve to one we actually have. Previously only ru was loaded, and choosing
// en or hy left the table empty, which made every label render as its own key.
const resolveLanguage = (lang) =>
	lang && Object.prototype.hasOwnProperty.call(translations, lang)
		? lang
		: DEFAULT_LANGUAGE;

const readStoredLanguage = () => {
	try {
		return resolveLanguage(localStorage.getItem("lang"));
	} catch {
		// Blocked or unavailable storage must not stop the panel from rendering.
		return DEFAULT_LANGUAGE;
	}
};

// Language data
export const languageOptions = [
	{ value: "en", label: "English", icon: en },
	{ value: "ru", label: "Русский", icon: ru },
	{ value: "hy", label: "Հայերեն", icon: hy },
];

const TranslationContext = createContext();

export const TranslationProvider = ({ children }) => {
	// Read the stored choice on mount: changeLanguage always wrote it, but nothing
	// ever read it back, so the language reset to Russian on every reload.
	const [language, setLanguage] = useState(readStoredLanguage);
	const [currentTranslations, setCurrentTranslations] = useState(
		() => translations[readStoredLanguage()],
	);

	useEffect(() => {
		setCurrentTranslations(translations[resolveLanguage(language)]);
	}, [language]);

	const changeLanguage = (lang) => {
		const next = resolveLanguage(lang);
		setLanguage(next);
		try {
			localStorage.setItem("lang", next);
		} catch {
			// The choice simply will not persist; not worth breaking the click over.
		}
	};

	// Fall back to Russian before giving up. A key that is missing from a
	// translation file must never surface in the UI as its own code.
	const t = (key) =>
		currentTranslations[key] ?? translations[DEFAULT_LANGUAGE][key] ?? key;

	return (
		<TranslationContext.Provider
			value={{ language, changeLanguage, t, languageOptions }}
		>
			{children}
		</TranslationContext.Provider>
	);
};

export const useTranslation = () => useContext(TranslationContext);
